import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { API_URL } from '../../../config/api';
import { uploadFilesWithProgress, inferAttachmentFileType } from '../../../utils/uploadFilesWithProgress';
import { downloadAttachment, formatFileSize } from '../../../utils/fileUtils';
import {
  addDestinationFile,
  createDestination,
  deleteDestination,
  fetchDestinations,
  removeDestinationFile,
} from '../services/travelService';

const SECTIONS = [
  {
    id: 'ITINERARY',
    title: 'Upload itinerary',
    hint: 'Full trip plan, day-by-day schedule, or combined travel pack.',
  },
  {
    id: 'TICKETS',
    title: 'Tickets',
    hint: 'Flights, trains, buses, and other boarding documents.',
  },
  {
    id: 'STAY',
    title: 'Stay',
    hint: 'Hotel, Airbnb, or other lodging confirmations.',
  },
];

const TravelHomePanel = ({ onClose }) => {
  const [destinations, setDestinations] = useState([]);
  const [selected, setSelected] = useState(null);
  const [nameDraft, setNameDraft] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [ticketPromptOpen, setTicketPromptOpen] = useState(false);
  const [pendingSection, setPendingSection] = useState(null);
  const fileInputRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const list = await fetchDestinations();
      setDestinations(list);
      setSelected((current) => {
        if (!current) return null;
        return list.find((item) => item.id === current.id) || null;
      });
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Failed to load travel');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const filesBySection = useMemo(() => {
    const files = selected?.files || [];
    const grouped = { ITINERARY: [], TICKETS: [], STAY: [] };
    files.forEach((file) => {
      const key = String(file.section || '').toUpperCase();
      if (grouped[key]) grouped[key].push(file);
    });
    return grouped;
  }, [selected]);

  const handleCreate = async (event) => {
    event.preventDefault();
    const name = nameDraft.trim();
    if (!name) {
      setError('Enter a destination name.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      const created = await createDestination(name);
      setNameDraft('');
      setDestinations((prev) => [created, ...prev.filter((item) => item.id !== created.id)]);
      setSelected(created);
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Could not create destination');
    } finally {
      setBusy(false);
    }
  };

  const handleDeleteDestination = async (destination) => {
    setBusy(true);
    setError('');
    try {
      await deleteDestination(destination.id);
      setDestinations((prev) => prev.filter((item) => item.id !== destination.id));
      if (selected?.id === destination.id) setSelected(null);
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Could not delete destination');
    } finally {
      setBusy(false);
    }
  };

  const openUpload = (section) => {
    setPendingSection(section);
    setTicketPromptOpen(false);
    window.setTimeout(() => fileInputRef.current?.click(), 0);
  };

  const handleSectionClick = (sectionId) => {
    if (sectionId === 'TICKETS') {
      setTicketPromptOpen(true);
      return;
    }
    openUpload(sectionId);
  };

  const handleFilesChosen = async (event) => {
    const list = Array.from(event.target.files || []);
    event.target.value = '';
    const section = pendingSection;
    setPendingSection(null);
    if (!selected?.id || !section || list.length === 0) return;
    setBusy(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      const results = await uploadFilesWithProgress({ apiUrl: API_URL, token, files: list });
      let next = selected;
      for (let index = 0; index < results.length; index += 1) {
        const data = results[index]?.data || {};
        const file = list[index];
        const fileUrl = data.fileUrl || data.fileKey || data.url || '';
        if (!fileUrl) continue;
        next = await addDestinationFile(selected.id, {
          section,
          fileName: data.fileName || file?.name || 'file',
          fileUrl,
          fileType: data.fileType || inferAttachmentFileType({ fileName: file?.name }),
          fileSize: data.fileSize || file?.size || 0,
        });
      }
      setSelected(next);
      setDestinations((prev) => prev.map((item) => (item.id === next.id ? next : item)));
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Upload failed');
    } finally {
      setBusy(false);
    }
  };

  const handleRemoveFile = async (file) => {
    if (!selected?.id || !file?.id) return;
    setBusy(true);
    setError('');
    try {
      const next = await removeDestinationFile(selected.id, file.id);
      setSelected(next);
      setDestinations((prev) => prev.map((item) => (item.id === next.id ? next : item)));
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Could not remove file');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="relative flex flex-col h-full min-h-0 bg-white dark:bg-gray-900">
      <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-gray-200 dark:border-gray-700">
        <div className="min-w-0">
          {selected ? (
            <button
              type="button"
              onClick={() => setSelected(null)}
              className="text-sm font-semibold text-emerald-600 dark:text-emerald-400 hover:underline"
            >
              ← All destinations
            </button>
          ) : (
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Travel</h2>
          )}
          {selected ? (
            <p className="text-lg font-semibold text-gray-900 dark:text-white truncate">{selected.name}</p>
          ) : (
            <p className="text-sm text-gray-500 dark:text-gray-400">Save itineraries, tickets, and stays by destination.</p>
          )}
        </div>
        <button
          type="button"
          onClick={onClose}
          className="px-3 py-1.5 text-sm rounded-lg bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200"
        >
          Close
        </button>
      </div>

      {error ? (
        <p className="px-5 py-2 text-sm text-red-600 dark:text-red-400">{error}</p>
      ) : null}

      <input
        ref={fileInputRef}
        type="file"
        multiple
        className="hidden"
        onChange={handleFilesChosen}
      />

      <div className="flex-1 min-h-0 overflow-y-auto p-5">
        {loading ? (
          <p className="text-sm text-gray-500">Loading travel…</p>
        ) : selected ? (
          <div className="space-y-5">
            <div className="flex flex-wrap gap-2">
              {SECTIONS.map((section) => (
                <button
                  key={`tag-${section.id}`}
                  type="button"
                  disabled={busy}
                  onClick={() => handleSectionClick(section.id)}
                  className="inline-flex items-center gap-1 rounded-full border border-blue-200 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-1.5 text-sm font-semibold text-blue-700 dark:text-blue-300"
                >
                  + {section.id === 'ITINERARY' ? 'Upload itinerary' : section.id === 'TICKETS' ? 'Add ticket' : 'Add stay'}
                </button>
              ))}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {SECTIONS.map((section) => {
              const files = filesBySection[section.id] || [];
              if (!files.length) return null;
              return (
                <section
                  key={section.id}
                  className="flex flex-col rounded-2xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/60 p-4"
                >
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => handleSectionClick(section.id)}
                    className="text-left"
                  >
                    <p className="text-base font-semibold text-emerald-700 dark:text-emerald-400 underline decoration-emerald-500/70">
                      {section.title}
                    </p>
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{section.hint}</p>
                  </button>
                  <div className="mt-3 space-y-2 flex-1">
                    {files.length === 0 ? (
                      <p className="text-xs text-gray-400">Nothing uploaded yet.</p>
                    ) : (
                      files.map((file) => (
                        <div
                          key={file.id}
                          className="flex items-start justify-between gap-2 rounded-lg bg-white dark:bg-gray-900 px-3 py-2 border border-gray-200 dark:border-gray-700"
                        >
                          <button
                            type="button"
                            className="min-w-0 text-left text-sm text-gray-800 dark:text-gray-100 truncate hover:underline"
                            onClick={() => downloadAttachment({
                              fileName: file.fileName,
                              fileUrl: file.fileUrl,
                              fileType: file.fileType,
                            })}
                          >
                            {file.fileName}
                            <span className="block text-[11px] text-gray-400">{formatFileSize(file.fileSize)}</span>
                          </button>
                          <button
                            type="button"
                            disabled={busy}
                            onClick={() => handleRemoveFile(file)}
                            className="text-xs text-red-500 hover:underline"
                          >
                            Remove
                          </button>
                        </div>
                      ))
                    )}
                  </div>
                </section>
              );
            })}
            </div>
          </div>
        ) : (
          <div className="space-y-5">
            <form onSubmit={handleCreate} className="flex flex-col sm:flex-row gap-2">
              <input
                value={nameDraft}
                onChange={(e) => setNameDraft(e.target.value)}
                placeholder="Destination name (city, trip, or country)"
                className="flex-1 px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-white"
              />
              <button
                type="submit"
                disabled={busy}
                className="px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 disabled:opacity-50"
              >
                Add destination
              </button>
            </form>
            {destinations.length === 0 ? (
              <p className="text-sm text-gray-500">No destinations yet. Add one to upload itinerary, tickets, and stay.</p>
            ) : (
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {destinations.map((destination) => {
                  const count = Array.isArray(destination.files) ? destination.files.length : 0;
                  return (
                    <li key={destination.id}>
                      <div className="flex items-stretch rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-hidden">
                        <button
                          type="button"
                          onClick={() => setSelected(destination)}
                          className="flex-1 text-left px-4 py-4"
                        >
                          <p className="font-semibold text-gray-900 dark:text-white">{destination.name}</p>
                          <p className="text-xs text-gray-500 mt-1">
                            {count} file{count === 1 ? '' : 's'} · itinerary, tickets, stay
                          </p>
                        </button>
                        <button
                          type="button"
                          disabled={busy}
                          onClick={() => handleDeleteDestination(destination)}
                          className="px-3 text-xs text-red-500 hover:bg-red-50 dark:hover:bg-red-950/30"
                        >
                          Delete
                        </button>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        )}
      </div>

      {ticketPromptOpen ? (
        <div className="absolute inset-0 z-20 flex items-center justify-center p-4">
          <button
            type="button"
            className="absolute inset-0 bg-black/50"
            aria-label="Close"
            onClick={() => setTicketPromptOpen(false)}
          />
          <div className="relative z-10 w-full max-w-md rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 p-5 shadow-2xl">
            <h3 className="text-base font-semibold text-gray-900 dark:text-white">Tickets</h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Upload boarding tickets only, or a complete travel itinerary for this destination.
            </p>
            <div className="mt-4 grid grid-cols-1 gap-2">
              <button
                type="button"
                disabled={busy}
                onClick={() => openUpload('TICKETS')}
                className="px-4 py-3 rounded-xl bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700"
              >
                Upload tickets
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={() => openUpload('ITINERARY')}
                className="px-4 py-3 rounded-xl border border-gray-300 dark:border-gray-600 text-sm font-semibold text-gray-800 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-800"
              >
                Upload complete travel itinerary
              </button>
              <button
                type="button"
                onClick={() => setTicketPromptOpen(false)}
                className="px-4 py-2 text-sm text-gray-500 hover:underline"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default TravelHomePanel;
